import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket } from "lucide-react";

export const Route = createFileRoute("/terms-and-conditions")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, follow" },
      { title: "Terms & Conditions — Three Day Launch" },
      { name: "description", content: "Terms and Conditions for using the Three Day Launch website and digital products." },
      { property: "og:title", content: "Terms & Conditions — Three Day Launch" },
      { property: "og:description", content: "Terms and Conditions for using the Three Day Launch website and digital products." },
      { property: "og:url", content: "https://threedaylaunch.com/terms-and-conditions" },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/terms-and-conditions" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Terms &amp; Conditions</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last Updated: 4/8/2026</p>

        <div className="mt-10 space-y-10 text-foreground/90 leading-relaxed">
          <p>By accessing this website or purchasing any product, you agree to the following Terms &amp; Conditions.</p>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Use of This Website</h2>
            <p>By using this website, you agree to use it for lawful purposes only and not for any illegal or unauthorized activities.</p>
            <p className="mt-3">You agree not to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Copy or reproduce materials without permission</li>
              <li>Share purchased content with others</li>
              <li>Resell or distribute this product</li>
              <li>Attempt to reverse engineer materials</li>
              <li>Use content for unlawful purposes</li>
            </ul>
            <p className="mt-3">All content is protected by copyright.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Digital Product Access</h2>
            <p>When you purchase a product, you will receive access to digital materials. Access is granted for your personal use only.</p>
            <p className="mt-3">You may not:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Share login access</li>
              <li>Distribute files</li>
              <li>Upload materials to other platforms</li>
              <li>Resell or sublicense content</li>
            </ul>
            <p className="mt-3">Violation may result in revoked access without refund.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">No Guarantees</h2>
            <p>We make no guarantees regarding:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Business results</li>
              <li>Income or revenue</li>
              <li>Website performance</li>
              <li>Lead generation results</li>
              <li>Marketing outcomes</li>
            </ul>
            <p className="mt-3">Your results depend on your effort, experience, and implementation.</p>
            <p className="mt-3">This product is for educational purposes only.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Earnings Disclaimer</h2>
            <p>Any examples of results shown are not guarantees of income or success.</p>
            <p className="mt-3">We do not promise that you will earn money, gain clients, or achieve specific results.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">AI Disclaimer</h2>
            <p>This product may include guidance using AI tools.</p>
            <p className="mt-3">AI-generated outputs:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>May vary</li>
              <li>May require editing</li>
              <li>Are not guaranteed to be accurate</li>
              <li>Should be reviewed before use</li>
            </ul>
            <p className="mt-3">You are responsible for reviewing and approving all content before publishing.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Refund Policy</h2>
            <p>Due to the digital nature of this product, all sales are final unless otherwise stated.</p>
            <p className="mt-3">If a refund policy is provided on the checkout page, that policy will apply.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Payment</h2>
            <p>You agree to provide accurate payment information.</p>
            <p className="mt-3">If payment fails or is disputed, access to the product may be revoked.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Intellectual Property</h2>
            <p>All materials in this product including:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Videos</li>
              <li>Templates</li>
              <li>Prompts</li>
              <li>Copywriting</li>
              <li>Designs</li>
              <li>Frameworks</li>
            </ul>
            <p className="mt-3">are owned by Three Day Launch.</p>
            <p className="mt-3">You are granted a limited, non-transferable license for personal use only.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Limitation of Liability</h2>
            <p>We are not responsible for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Business losses</li>
              <li>Website issues</li>
              <li>Platform outages</li>
              <li>AI tool changes</li>
              <li>Technical problems</li>
              <li>Lost revenue</li>
            </ul>
            <p className="mt-3">Use of this product is at your own risk.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Third-Party Platforms</h2>
            <p>This product may reference third-party platforms, tools, or services.</p>
            <p className="mt-3">We are not responsible for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Platform changes</li>
              <li>Pricing updates</li>
              <li>Feature changes</li>
              <li>Availability</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Changes to Terms</h2>
            <p>We may update these Terms at any time.</p>
            <p className="mt-3">By continuing to use this website, you agree to the updated Terms.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Contact</h2>
            <p>If you have questions, contact:</p>
            <p className="mt-3 font-medium">Three Day Launch</p>
          </section>
        </div>
      </main>

      <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10 mt-16">
        <div className="mx-auto max-w-7xl px-6 text-center text-sm text-white/80">
          © {new Date().getFullYear()} Three Day Launch. Custom websites for business owners.
        </div>
      </footer>
    </div>
  );
}
